import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Loader2, Info, ChevronLeft } from 'lucide-react';
import { uploadItineraryFile } from '../services/adminService';
import { UploadResult, ValidationError, UploadProgress, InformationalMessage } from '../types/itinerary';
import toast from 'react-hot-toast';

const AdminItineraryUpload = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.json')) {
      toast.error('Please select a CSV or JSON file');
      return;
    }
    
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setSelectedFile(file);
    setUploadResult(null);
    setUploadProgress(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragleave' || e.type === 'dragover') {
      setDragActive(e.type !== 'dragleave');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    setUploadProgress(null);
    
    try {
      const result = await uploadItineraryFile(selectedFile, (progress) => {
        setUploadProgress(progress);
      });
      setUploadResult(result);
      setUploadProgress(null);
      
      if (result.success) {
        toast.success(`Upload completed: ${result.newRecords} new, ${result.updatedRecords} updated`);
      } else {
        toast.error(`Upload completed with errors. Check details below.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setUploadResult(null);
  };

  const renderErrors = (errors: ValidationError[]) => {
    if (errors.length === 0) return null;
    
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold text-red-600 mb-2 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Errors ({errors.length})
        </h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {errors.map((error, index) => (
              <div key={index} className="text-sm">
                <span className="font-medium text-red-800">Row {error.row}</span>
                {error.recordType && (
                  <span className="text-red-700 ml-1">({error.recordType})</span>
                )}
                {error.field && (
                  <span className="text-red-700 ml-1">- Field "{error.field}"</span>
                )}
                <span className="text-red-600 ml-1">: {error.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderInformationalMessages = (messages: InformationalMessage[]) => {
    if (messages.length === 0) return null;
    
    return (
      <div className="mt-4">
        <h3 className="text-lg font-semibold text-blue-600 mb-2 flex items-center">
          <Info className="h-5 w-5 mr-2" />
          Key Updates ({messages.length})
        </h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {messages.map((message, index) => (
              <div key={index} className="text-sm">
                {message.type === 'ship_added' && (
                  <span className="text-blue-800">
                    <span className="font-medium">Ship Added:</span> "{message.shipName}" added to {message.cruiseLine} ships list
                  </span>
                )}
                {message.type === 'ship_case_corrected' && (
                  <span className="text-blue-800">
                    <span className="font-medium">Ship Name Corrected:</span> "{message.oldShipName}" updated to "{message.shipName}" for {message.cruiseLine}
                  </span>
                )}
                {message.type === 'ship_url_added' && (
                  <span className="text-blue-800">
                    <span className="font-medium">Ship URL Added:</span> {message.urlType === 'locationUrl' ? 'Location' : message.urlType === 'infoUrl' ? 'Info' : 'Deckplan'} URL added for "{message.shipName}" ({message.cruiseLine})
                    {message.url && (
                      <span className="ml-1 text-blue-600 text-xs break-all">({message.url})</span>
                    )}
                  </span>
                )}
                {message.type === 'ship_url_updated' && (
                  <span className="text-blue-800">
                    <span className="font-medium">Ship URL Updated:</span> {message.urlType === 'locationUrl' ? 'Location' : message.urlType === 'infoUrl' ? 'Info' : 'Deckplan'} URL updated for "{message.shipName}" ({message.cruiseLine})
                    {message.url && (
                      <span className="ml-1 text-blue-600 text-xs break-all">({message.url})</span>
                    )}
                  </span>
                )}
                {message.type === 'ship_metadata_updated' && (
                  <span className="text-blue-800">
                    <span className="font-medium">Ship Metadata Updated:</span> Ship metadata updated for "{message.shipName}" ({message.cruiseLine})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Upload className="h-8 w-8 text-teal-600" />
            Itinerary Upload
          </h1>
          <p className="text-gray-600 mt-1">Upload cruise itinerary CSV or JSON files</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        {/* File Upload Area */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select CSV or JSON File
          </label>
          
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-teal-500 bg-teal-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            {selectedFile ? (
              <div className="flex items-center justify-center space-x-3">
                <FileText className="h-8 w-8 text-teal-500" />
                <div className="text-left">
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                </div>
                <button
                  onClick={handleClear}
                  className="ml-4 text-gray-400 hover:text-red-500"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div>
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">
                  Drag and drop a CSV or JSON file here, or click to browse
                </p>
                <label className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 cursor-pointer">
                  <FileText className="h-4 w-4 mr-2" />
                  Browse Files
                  <input
                    type="file"
                    accept=".csv,.json"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Upload Button */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClear}
            disabled={uploading || !selectedFile}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !selectedFile}
            className="flex items-center px-6 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading && (!uploadProgress || uploadProgress.stage === 'reading') ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-5 w-5 mr-2" />
                Upload File
              </>
            )}
          </button>
        </div>

        {/* Processing View */}
        {uploading && uploadProgress && uploadProgress.stage !== 'reading' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 border-t border-gray-200 pt-6"
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Processing Upload</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Loader2 className="h-6 w-6 text-blue-500 animate-spin mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">
                    {uploadProgress.message}
                  </h3>
                  
                  {uploadProgress.details && (
                    <p className="text-sm text-blue-700 mb-3">
                      {uploadProgress.details}
                    </p>
                  )}
                  
                  {uploadProgress.current !== undefined && uploadProgress.total !== undefined && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-blue-700 mb-2">
                        <span>Progress</span>
                        <span>{uploadProgress.current} / {uploadProgress.total}</span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2.5">
                        <motion.div
                          className="bg-blue-600 h-2.5 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${(uploadProgress.current / uploadProgress.total) * 100}%` 
                          }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}% complete
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 border-t border-gray-200 pt-6"
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Results</h2>
            
            {/* Summary */}
            <div className="mb-6">
              {/* Total Rows - Show first */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Total Rows Imported</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex flex-col h-full">
                    <div className="flex items-center mb-2">
                      <FileText className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0" />
                      <p className="text-sm text-purple-700">Total Rows</p>
                    </div>
                    <p className="text-2xl font-bold text-purple-900 mt-auto">{uploadResult.summary.totalFileRows}</p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col h-full">
                    <div className="flex items-center mb-2">
                      <FileText className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                      <p className="text-sm text-blue-700">Itinerary Records</p>
                    </div>
                    <p className="text-2xl font-bold text-blue-900 mt-auto">{uploadResult.summary.processedRows + uploadResult.summary.skippedRows + uploadResult.summary.errorRows}</p>
                  </div>
                  
                  {uploadResult.summary.shipRows > 0 && (
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex flex-col h-full">
                      <div className="flex items-center mb-2">
                        <FileText className="h-5 w-5 text-teal-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-teal-700">Ship Records</p>
                      </div>
                      <p className="text-2xl font-bold text-teal-900 mt-auto">{uploadResult.summary.shipRows}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Itinerary Records */}
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Itinerary Records</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex flex-col h-full">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-green-700">New Itineraries</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900 mt-auto">{uploadResult.newRecords}</p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col h-full">
                  <div className="flex items-center mb-2">
                    <FileText className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-blue-700">Updated Itineraries</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900 mt-auto">{uploadResult.updatedRecords}</p>
                </div>
                
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col h-full">
                  <div className="flex items-center mb-2">
                    <FileText className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700">Skipped</p>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 mt-auto">{uploadResult.summary.skippedRows + uploadResult.summary.headerRow}</p>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col h-full">
                  <div className="flex items-center mb-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                    <p className="text-sm text-red-700">Error Rows</p>
                  </div>
                  <p className="text-2xl font-bold text-red-900 mt-auto">{uploadResult.summary.errorRows}</p>
                </div>
              </div>
              
              {/* Ship Records */}
              {uploadResult.summary.shipRows > 0 && (
                <>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6">Ship Records</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex flex-col h-full">
                      <div className="flex items-center mb-2">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-green-700">New Ships</p>
                      </div>
                      <p className="text-2xl font-bold text-green-900 mt-auto">{uploadResult.newShipRecords}</p>
                    </div>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex flex-col h-full">
                      <div className="flex items-center mb-2">
                        <FileText className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-blue-700">Updated Ships</p>
                      </div>
                      <p className="text-2xl font-bold text-blue-900 mt-auto">{uploadResult.updatedShipRecords}</p>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col h-full">
                      <div className="flex items-center mb-2">
                        <FileText className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-gray-700">Skipped Ships</p>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 mt-auto">{uploadResult.summary.skippedShipRows}</p>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col h-full">
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-red-700">Error Ships</p>
                      </div>
                      <p className="text-2xl font-bold text-red-900 mt-auto">{uploadResult.summary.errorShipRows}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Header info alert - only show for CSV files */}
            {uploadResult.summary.headerRow > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <Info className="h-4 w-4 text-blue-500 mr-2" />
                  <p className="text-sm text-blue-800">
                    Header row (row 1) was skipped and counted in the skipped total.
                  </p>
                </div>
              </div>
            )}

            {/* Status */}
            <div className={`p-4 rounded-lg mb-4 ${
              uploadResult.success
                ? 'bg-green-50 border border-green-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center">
                {uploadResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                )}
                <p className={`font-medium ${
                  uploadResult.success ? 'text-green-800' : 'text-yellow-800'
                }`}>
                  {uploadResult.success
                    ? 'Upload completed successfully'
                    : 'Upload completed with errors'}
                </p>
              </div>
            </div>

            {/* Errors */}
            {renderErrors(uploadResult.errors)}
            
            {/* Informational Messages */}
            {renderInformationalMessages(uploadResult.informationalMessages)}
          </motion.div>
        )}

        {/* Instructions */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">File Format Requirements</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-blue-800 mb-2">Supported Formats: CSV or JSON</p>
            <p className="text-sm font-semibold text-blue-800 mb-2">JSON Format:</p>
            <p className="text-sm text-blue-800 mb-2">The JSON file can contain two types of records:</p>
            <p className="text-sm font-semibold text-blue-800 mb-1 mt-3">1. Itinerary Records (default or recordType: \"itinerary\"):</p>
            <ul className="list-disc list-inside space-y-2 text-sm text-blue-800 mb-4 ml-4">
              <li>Array of objects with cruise itinerary data</li>
              <li>Summary entries (type: \"cruise_line_summary\") are automatically skipped and counted in the skipped total</li>
              <li><strong>Required fields:</strong> cruiseLine, shipName, sailingDate, returnDate, departurePort</li>
              <li><strong>Optional fields:</strong> itineraryTitle, cruiseLength, shipLocation, shipUrl, shipDeckplan, portsOfCall</li>
              <li><strong>portsOfCall:</strong> Array of objects with portName, portDate, arrivalTime (optional), departureTime (optional)</li>
              <li><strong>Date format:</strong> YYYY-MM-DD</li>
            </ul>
            <p className="text-sm font-semibold text-blue-800 mb-1 mt-3">2. Ship Records (recordType: \"ship\"):</p>
            <ul className="list-disc list-inside space-y-2 text-sm text-blue-800 mb-4 ml-4">
              <li>Records containing ship metadata information</li>
              <li><strong>Required fields:</strong> recordType: \"ship\", shipName, cruiseLine</li>
              <li><strong>Optional fields:</strong> shipUrl, shipLocation, shipDeckplan, yearBuilt, lastRefurbishment, shipClass, grossTonnage, decks, length, width, speed, passengers, crew, cabin, formerNames, builder, flagState, extractedAt</li>
              <li>Ship records are processed separately and stored in shipMetaData in Firebase</li>
              <li>Ship records are counted separately from itinerary records in the reconciliation summary</li>
            </ul>
            <p className="text-sm font-semibold text-blue-800 mb-2">CSV Format:</p>
            <ul className="list-disc list-inside space-y-2 text-sm text-blue-800">
              <li><strong>Required columns:</strong> cruiseLine, shipName, sailingDate, returnDate, departurePort</li>
              <li><strong>Optional columns:</strong> itineraryTitle, cruiseLength, shipLocation, shipUrl</li>
              <li><strong>Date format:</strong> YYYY-MM-DD or MM/DD/YYYY (e.g., 2025-06-15 or 06/15/2025)</li>
              <li><strong>Ports of Call - Two formats supported:</strong></li>
              <li className="ml-4">1. <strong>JSON format:</strong> portsOfCall column with JSON array string, e.g., {'[{"name":"Nassau","date":"2025-06-16","arrivalTime":"08:00","departureTime":"17:00"}]'}</li>
              <li className="ml-4">2. <strong>Column-based format:</strong> portsOfCall/0/portName, portsOfCall/0/portDate, portsOfCall/0/arrivalTime, portsOfCall/0/departureTime, portsOfCall/1/portName, etc. (supports multiple ports)</li>
              <li className="ml-4">Column-based format is case-insensitive and supports unlimited ports</li>
            </ul>
            <p className="text-sm text-blue-800 mt-4"><strong>File size limit:</strong> 10MB</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminItineraryUpload;


